// Customer directory API surface (PDF round 2 item 5).
//
// A "customer" is a per-merchant identity row keyed on (email, phone)
// — migration 025 introduced this so CSV uploads can identify a payee
// by contact details rather than account number + IFSC.
//
// Read-only on the panel today; creates happen server-side via
// AddBeneficiary / CSV upload through the customer-resolution path.
import { apiClient } from "./client";

/**
 * LinkedBeneficiary — the wire shape returned by the customer detail
 * endpoint for the customer's bank accounts. Distinct from
 * api/beneficiaries.ts's Beneficiary (which renames `name` → `label`
 * for back-compat with the validation table) because the customer
 * detail page wants the raw display name verbatim.
 */
export interface LinkedBeneficiary {
  id: number;
  merchant_id: number;
  name: string;
  account_no: string;
  ifsc: string;
  email: string | null;
  phone: string | null;
  is_registered: boolean;
  verified_at: string | null;
  created_at: string;
}

export interface Customer {
  id: number;
  merchant_id: number;
  email: string;
  phone: string;
  name: string;
  created_at: string;
  updated_at: string;
  /** Number of distinct bank accounts (beneficiary rows) linked to this customer. */
  beneficiary_count: number;
}

export interface ListCustomersParams {
  /** Substring search across email/phone/name (ILIKE on the backend). */
  q?: string;
  /** Cursor — returns rows with id > after, ascending. */
  after?: number;
  /** Page size, default 50, max 200. */
  limit?: number;
}

/**
 * listCustomers — GET /v1/merchants/:merchantId/customers
 *
 * Returns the merchant's customer directory. Viewer+ at the membership
 * layer; admin short-circuits.
 */
export async function listCustomers(
  merchantId: number,
  params: ListCustomersParams = {},
): Promise<{ customers: Customer[]; limit: number }> {
  const search = new URLSearchParams();
  if (params.q) search.set("q", params.q);
  if (params.after != null) search.set("after", String(params.after));
  if (params.limit != null) search.set("limit", String(params.limit));
  const qs = search.toString();
  const resp = await apiClient.get<{ customers: Customer[]; limit: number }>(
    `/v1/merchants/${merchantId}/customers${qs ? `?${qs}` : ""}`,
  );
  return resp.data;
}

export interface CustomerDetail {
  customer: Customer;
  /**
   * Linked bank accounts — every Beneficiary row with this customer_id.
   * Sorted oldest-first by created_at on the backend so the original
   * registration sits on top.
   */
  beneficiaries: LinkedBeneficiary[];
}

/**
 * getCustomer — GET /v1/merchants/:merchantId/customers/:customerId
 *
 * Returns the customer record + all linked beneficiaries. Viewer+.
 */
export async function getCustomer(
  merchantId: number,
  customerId: number,
): Promise<CustomerDetail> {
  const resp = await apiClient.get<CustomerDetail>(
    `/v1/merchants/${merchantId}/customers/${customerId}`,
  );
  return resp.data;
}
